import React, { useCallback, useEffect } from "react";

import { object, string, number, date, InferType } from 'yup';
import snakeToPascal, { revertLocaleString } from "../../../tools";
import CustomDatePicker from "./reusables/CustomDatePicker";
import { initTimeSerie } from "./Initializer";
import apiService from "../../../services/ApiService";
import dayjs from "dayjs";
import { useAppStore } from "../../../shared/stores/useAppStore";
import { DATESYSTEM_TO_FORMAT } from "../../../pages/subpages/Settings";
import { TailwindDataGrid, TailwindSelect, TailwindModal, TailwindButton } from "../../Tailwind";
import type { TailwindDataGridColumn } from "../../Tailwind/TailwindDataGrid";
import { useSaveIndicator } from "../../../hooks/useSaveIndicator";
import SaveIndicator from "../../SaveIndicator";
import FieldError from "../FieldError";
import { useEditorStore } from "../../../shared/stores/useEditorStore";
import { useQueryClient } from "@tanstack/react-query";
import { SECTION_DATA_KEY } from "../../../shared/hooks/queries/useSectionLoader";

const PERIODICITY_RANK = { 'monthly': 1, 'quarterly': 2, 'semi-annually': 3, 'annually': 4 };

type ConversionMode = 'divide' | 'copy' | 'sum' | 'average';

interface PendingPeriodicityChange {
    direction: 'coarseToFine' | 'fineToCoarse';
    oldPeriodicity: string;
    newPeriodicity: string;
    oldData: { date: string; endOfPeriod: string; value: number | null }[];
    newData: { date: { value: string }; end_of_period: { value: string }; value: { value: number | null } }[];
}

const aggregateValues = (
    oldData: { date: string; endOfPeriod: string; value: number | null }[],
    newData: { date: { value: string }; end_of_period: { value: string }; value: { value: number | null } }[],
    oldPeriodicity: string,
    newPeriodicity: string,
    mode?: ConversionMode,
): { date: string; value: number | null }[] => {
    const oldRank = PERIODICITY_RANK[oldPeriodicity] ?? 0;
    const newRank = PERIODICITY_RANK[newPeriodicity] ?? 0;

    if (oldRank === newRank || oldRank === 0 || newRank === 0) return [];

    const hasAnyValue = oldData.some(e => e.value != null);
    if (!hasAnyValue) return [];

    if (oldRank < newRank) {
        // Fine → Coarse: SUM ou AVERAGE
        const effectiveMode = mode || 'sum';
        return newData.map(np => {
            const npDate = np.date.value;
            const npEnd = np.end_of_period.value;
            let sum = 0;
            let count = 0;
            for (const op of oldData) {
                if (op.date >= npDate && op.date <= npEnd && op.value != null) {
                    sum += op.value;
                    count++;
                }
            }
            if (count === 0) return { date: npDate, value: null };
            return { date: npDate, value: effectiveMode === 'average' ? sum / count : sum };
        });
    } else {
        // Coarse → Fine: DIVIDE ou COPY
        const effectiveMode = mode || 'divide';
        const countMap: Record<string, number> = {};
        const mappingMap: Record<string, string> = {};
        for (const np of newData) {
            for (const op of oldData) {
                if (np.date.value >= op.date && np.date.value <= op.endOfPeriod) {
                    mappingMap[np.date.value] = op.date;
                    countMap[op.date] = (countMap[op.date] || 0) + 1;
                    break;
                }
            }
        }
        const oldMap = Object.fromEntries(oldData.map(e => [e.date, e.value]));
        return newData.map(np => {
            const oldDate = mappingMap[np.date.value];
            if (!oldDate) return { date: np.date.value, value: null };
            const oldValue = oldMap[oldDate];
            if (oldValue == null) return { date: np.date.value, value: null };
            return { date: np.date.value, value: effectiveMode === 'copy' ? oldValue : oldValue / countMap[oldDate] };
        });
    }
};

export default function TimeSerieField({ name, field }) {
    const userSettings = useAppStore(s => s.userSettings);
    const isReadOnly = !useEditorStore(s => s.userRights.right_to_write);
    const scenarioId = useEditorStore(s => s.scenarioId) ?? 0;
    const queryClient = useQueryClient();

    const invalidateQueries = () => {
        // Only invalidate section-data cache so stale sections are refetched on next
        // open.  Do NOT invalidate SCENARIO_TREE_KEY: its queryFn returns an empty
        // initial state, so an invalidation-triggered refetch would wipe the
        // lazily-loaded tree and cause model-data items to disappear.
        queryClient.invalidateQueries({ queryKey: [SECTION_DATA_KEY, scenarioId] });
    };
    const [mounted, setMounted] = React.useState(false);
    // Identity of the cell currently being edited (lifted up so we can move
    // focus across cells with Tab/Shift+Tab without unmounting the input mid-
    // commit). One source of truth — each cell renders an input only when its
    // fieldKey matches.
    const [editingCellKey, setEditingCellKey] = React.useState<string | null>(null);
    const [focusedColumnIndex, setFocusedColumnIndex] = React.useState<number | null>(null);
    const { status: saveStatus, trigger: triggerSave, resolve: resolveSave } = useSaveIndicator();

    // Etat pour la modal de choix de conversion lors du changement de periodicite
    const [periodicityModalOpen, setPeriodicityModalOpen] = React.useState(false);
    const [pendingPeriodicityChange, setPendingPeriodicityChange] = React.useState<PendingPeriodicityChange | null>(null);

    const reloadLocalState = () => {
        setMounted(!mounted);
    }

    // Listen for agent field mutations that have already been applied to this
    // field's data by the FME handler. The FME dispatches 'agent:field_patched'
    // after the async fetch+mutation is complete. We just need to re-render.
    useEffect(() => {
        const handler = (e: Event) => {
            const detail = (e as CustomEvent).detail;
            if (detail?.fieldValueId === field.id) {
                setMounted(prev => !prev);
            }
        };
        window.addEventListener('agent:field_patched', handler);
        return () => window.removeEventListener('agent:field_patched', handler);
    }, [field.id]);

    const formatTimeSerieObj = (ts) => {
        const ret = {
            name: 'Value',
        }
    
        ts.currentValue.value.data.map(e => {
            const rawValue = e.value.value;
            const cellValue = rawValue != null ? (field.is_percentage ? rawValue * 100 : rawValue) : null;
            ret[e.date.value + '|' + e.end_of_period.value] = cellValue;
        })
        return ret
    }

    
    const renderNameCell = (params) => {
        return (
            <span className="font-bold">{snakeToPascal(params.row[params.field])}</span>
        )
    }

    const renderDateHeader = (value) => {
        const [bop, eop] = value.split('|');
        return (
            <div className="flex flex-col">
                <span className="font-bold">
                    {dayjs(bop).format(DATESYSTEM_TO_FORMAT[userSettings.dateSystem])}
                </span>
                <span className="font-bold">
                    {dayjs(eop).format(DATESYSTEM_TO_FORMAT[userSettings.dateSystem])}
                </span>
            </div>
        )
    }

    const renderNameHeader = () => {
        return (
            <div className="flex flex-col">
                <strong title='Beginning of Period'>
                    Start
                </strong>
                <strong title='End of Period'>
                    End
                </strong>
            </div>
        )
    }

    const handleMultiCellPaste = (text: string, startColumnIndex: number) => {
        const timeSerieDataLocal = formatTimeSerieObj(field);
        const dataKeys = Object.keys(timeSerieDataLocal).filter(k => k !== 'name');

        const lines = text.split(/\r?\n/).filter(l => l.trim() !== '');
        const values = (lines[0] || '').split('\t').map(v => v.trim());

        const elems: { date: string; value: number }[] = [];
        for (let i = 0; i < values.length; i++) {
            const colIdx = startColumnIndex + i;
            if (colIdx >= dataKeys.length) break;

            const dateKey = dataKeys[colIdx].split('|')[0];
            let parsed = parseFloat(values[i].replace(',', '.'));
            if (isNaN(parsed)) continue;

            if (field.is_percentage) parsed = parsed / 100;

            elems.push({ date: dateKey, value: parsed });
        }

        if (elems.length > 0) {
            triggerSave();
            apiService.putTimeSerieElems(field.id, elems).then((resp) => {
                if (resp) {
                    resolveSave();
                    updateTimeSerie();
                }
            });
        }
    };

    // Move the editing cursor one column forward / backward, or close it
    // entirely past the boundaries. Called from Tab / Shift+Tab inside a cell.
    const advanceEditingCell = (currentFieldKey: string, delta: 1 | -1) => {
        const dataKeys = Object.keys(formatTimeSerieObj(field)).filter(k => k !== 'name');
        const idx = dataKeys.indexOf(currentFieldKey);
        if (idx === -1) {
            setEditingCellKey(null);
            return;
        }
        const next = dataKeys[idx + delta];
        setEditingCellKey(next ?? null);
    };

    const EditableNumberCell = ({ cellValue, fieldKey, columnIndex }: { cellValue: any; fieldKey: string; columnIndex: number }) => {
        const displayValue = cellValue != null
            ? cellValue.toLocaleString(userSettings.numberFormattingLocale, { maximumFractionDigits: 5 })
            : '';
        const editing = editingCellKey === fieldKey;
        const [localValue, setLocalValue] = React.useState(displayValue);
        const inputRef = React.useRef<HTMLInputElement | null>(null);

        const localValueRef = React.useRef(localValue);
        const editingRef = React.useRef(false);
        const wasEditingRef = React.useRef(false);
        localValueRef.current = localValue;
        editingRef.current = editing;

        // Extract bop date from field key "2025-01-01|2025-12-31"
        const bopDate = fieldKey.split('|')[0];

        const commitIfChanged = () => {
            const rawStr = cellValue != null ? String(cellValue) : '';
            const lv = localValueRef.current;
            if (lv !== rawStr && lv !== displayValue) {
                let apiValue = lv;
                if (field.is_percentage && apiValue !== '') {
                    const num = parseFloat(apiValue.replace(',', '.'));
                    if (!isNaN(num)) {
                        apiValue = String(num / 100);
                    }
                }
                validateTimeSerieElemChange(apiValue, bopDate);
            }
        };

        // Save on unmount if still editing.
        React.useEffect(() => {
            return () => {
                if (editingRef.current) {
                    commitIfChanged();
                }
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        // Single commit point for any editing → not-editing transition. Covers
        // every exit path: blur, Tab, Enter, mouse-down on another cell. Doing
        // it here means we don't have to commit-and-then-set-state in each
        // handler; the new cell can grab editing in one mousedown without us
        // racing the React-handled blur of the previous input.
        React.useEffect(() => {
            if (wasEditingRef.current && !editing) {
                commitIfChanged();
            }
            wasEditingRef.current = editing;
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [editing]);

        // When this cell becomes editing (via lifted state), focus + select all
        // text so the user can immediately type to overwrite.
        React.useEffect(() => {
            if (editing) {
                setLocalValue(cellValue != null ? String(cellValue) : '');
                requestAnimationFrame(() => {
                    inputRef.current?.focus();
                    inputRef.current?.select();
                });
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [editing]);

        const handleBlur = () => {
            // Commit happens in the editing-transition effect above.
            setEditingCellKey(prev => (prev === fieldKey ? null : prev));
        };

        const handleInputPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
            const text = e.clipboardData.getData('text/plain');
            if (text.includes('\t')) {
                e.preventDefault();
                setEditingCellKey(null);
                handleMultiCellPaste(text, columnIndex);
            }
        };

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                (e.target as HTMLInputElement).blur();
            } else if (e.key === 'Tab') {
                e.preventDefault();
                advanceEditingCell(fieldKey, e.shiftKey ? -1 : 1);
            } else if (e.key === 'Escape') {
                e.preventDefault();
                // Restore display value, then close — the effect sees no diff
                // and skips the commit.
                setLocalValue(cellValue != null ? String(cellValue) : '');
                setEditingCellKey(null);
            }
        };

        // Use mousedown so we grab editing in the same event the previous
        // cell's input loses focus — otherwise React's blur-triggered
        // re-render eats the click and the user has to click twice.
        const handleDisplayMouseDown = (e: React.MouseEvent) => {
            // Avoid the data grid's own selection handling stealing the event.
            e.preventDefault();
            setFocusedColumnIndex(columnIndex);
            setEditingCellKey(fieldKey);
        };

        if (isReadOnly) {
            return (
                <div className="w-full h-full flex items-center justify-center">
                    {cellValue != null ? displayValue : <span style={{ color: '#999' }}>{field.placeholder}</span>}
                </div>
            );
        }

        return editing ? (
            <input
                ref={inputRef}
                type="text"
                value={localValue}
                onChange={(e) => setLocalValue(e.target.value)}
                onBlur={handleBlur}
                onPaste={handleInputPaste}
                onKeyDown={handleKeyDown}
                className="w-full h-full bg-transparent border-b border-slate-300 outline-none text-sm py-0.5 focus:border-primary text-center"
            />
        ) : (
            <div
                className="cursor-text w-full h-full flex items-center justify-center text-sm"
                onMouseDown={handleDisplayMouseDown}
            >
                {cellValue != null ? displayValue : <span style={{ color: '#999' }}>{field.placeholder}</span>}
            </div>
        );
    };

    const renderNumberCell = (value, columnIndex: number) => {
        return <EditableNumberCell cellValue={value.row[value.field]} fieldKey={value.field} columnIndex={columnIndex} />;
    }


    const updateTimeSerie = (onDataReady?: (newData: any[]) => void) => {
        apiService.getTimeSerie(field.id).then(async(response) => {
            if (response) {
                const newTs = await response.json();
                field.currentValue.value.data = newTs.data;
                initTimeSerie(field);
                if (onDataReady) {
                    onDataReady(field.currentValue.value.data);
                }
                reloadLocalState();
                invalidateQueries();
            }
        })
    }

    const [timingChoicesItems, setTimingChoicesItems] = React.useState([]);
    const [timingOptionsLoading, setTimingOptionsLoading] = React.useState(true);

    React.useEffect(() => {
        // Global library snapshot: its ids belong to another database.
        if (field.snapshot) {
            setTimingOptionsLoading(false);
            return;
        }
        setTimingOptionsLoading(true);
        apiService.getTimingOptions(field.id).then(async (response) => {
            if (response && response.ok) {
                const data = await response.json();
                const items = Array.isArray(data) ? data : [];
                setTimingChoicesItems(items);

                // Set timing choice by id if needed
                if (field.currentValue?.value?.timing_id) {
                    items.forEach((item: any) => {
                        if (item.id == field.currentValue.value.timing_id) {
                            if (field.currentValue.timingChoice == 'null') {
                                field.currentValue.timingChoice = item;
                                reloadLocalState();
                            }
                        }
                    });
                }
            } else {
                setTimingChoicesItems([]);
            }
            setTimingOptionsLoading(false);
        });
    }, [field.id]);

    const checkComplete = () => {
        if (field.currentValue.value.timing.start_date.value && field.currentValue.value.timing.end_date.value) {
            if (field.currentValue.value.timing.start_date.value.diff(field.currentValue.value.timing.end_date.value, 'day') >= 0) {
                field.complete = false;
                field.timingError = 'Start date must be before End date.'
            }
            else if (field.currentValue.value.timing.start_date.value.diff(field.currentValue.value.timing.end_date.value, 'month') == 0) {
                field.complete = false;
                field.timingError = 'Difference Between Dates should be at least one month.'
            }
            else if (field.currentValue.value.timing.periodicity.value !== 'null') {
                field.complete = true;
                field.timingError = null;
            }
            else {
                field.timingError = null;
                field.complete = false;
            }
        }
        else {
            field.complete = false;
            field.timingError = null;
        }
    }


    const handleChangestart_date = (newValue) => {

        const timing = field.currentValue.value.timing;

        triggerSave();
        apiService.putModelDataTimingValue(field.currentValue.value.timing_id,
            newValue.format('YYYY-MM-DD'),
            (timing.end_date.value) ? timing.end_date.value.format('YYYY-MM-DD') : null,
            timing.periodicity.value,
        ).then(async(response) => {
            if (response) {
                timing.start_date.value = newValue;
                field.start_dateStore.isOpen = false;
                checkComplete()
                resolveSave();
                updateTimeSerie();
            }
        })

    };

    const handleChangeend_date = (newValue) => {
        const timing = field.currentValue.value.timing;

        triggerSave();
        apiService.putModelDataTimingValue(field.currentValue.value.timing_id,
            (timing.start_date.value) ? timing.start_date.value.format('YYYY-MM-DD') : null,
            newValue.format('YYYY-MM-DD'),
            timing.periodicity.value,
        ).then(async(response) => {
            if (response) {
                timing.end_date.value = newValue;
                field.end_dateStore.isOpen = false;
                invalidateQueries();
                resolveSave();
                checkComplete()
                updateTimeSerie();
            }
        })
    };

    const handleChangePeriodicity = (e) => {
        const timing = field.currentValue.value.timing;
        const oldPeriodicity = timing.periodicity.value;
        const newPeriodicity = e.target.value;

        // Sauvegarder les valeurs actuelles avant l'appel API
        const oldData = field.currentValue.value.data.map(elem => ({
            date: String(elem.date.value),
            endOfPeriod: String(elem.end_of_period.value),
            value: elem.value.value,
        }));

        triggerSave();
        apiService.putModelDataTimingValue(field.currentValue.value.timing_id,
            (timing.start_date.value) ? timing.start_date.value.format('YYYY-MM-DD') : null,
            (timing.end_date.value) ? timing.end_date.value.format('YYYY-MM-DD') : null,
            newPeriodicity,
        ).then(async(response) => {
            if (response) {
                timing.periodicity.value = newPeriodicity;
                invalidateQueries();
                resolveSave();
                checkComplete();

                const oldRank = PERIODICITY_RANK[oldPeriodicity] ?? 0;
                const newRank = PERIODICITY_RANK[newPeriodicity] ?? 0;
                const hasAnyValue = oldData.some(e => e.value != null);

                if (hasAnyValue && oldRank !== newRank && oldRank > 0 && newRank > 0) {
                    // Il y a des valeurs existantes : afficher la modal de choix
                    const direction = oldRank > newRank ? 'coarseToFine' : 'fineToCoarse';
                    updateTimeSerie((newData) => {
                        setPendingPeriodicityChange({
                            direction,
                            oldPeriodicity,
                            newPeriodicity,
                            oldData,
                            newData,
                        });
                        setPeriodicityModalOpen(true);
                    });
                } else {
                    // Pas de valeurs existantes : pas de conversion necessaire
                    updateTimeSerie();
                }
            }
        })
    }

    // Applique le choix de conversion de periodicite
    const applyPeriodicityConversion = (mode: ConversionMode) => {
        if (!pendingPeriodicityChange) return;

        const { oldData, newData, oldPeriodicity, newPeriodicity } = pendingPeriodicityChange;
        const elems = aggregateValues(oldData, newData, oldPeriodicity, newPeriodicity, mode);

        if (elems.length > 0) {
            triggerSave();
            apiService.putTimeSerieElems(field.id, elems).then((resp) => {
                if (resp) {
                    resolveSave();
                    updateTimeSerie();
                }
            });
        }

        setPeriodicityModalOpen(false);
        setPendingPeriodicityChange(null);
    };

    const handlePeriodicityModalClose = () => {
        setPeriodicityModalOpen(false);
        setPendingPeriodicityChange(null);
    };

    const getElembyDate = (date) => {
        const [elem] = field.currentValue.value.data.filter(e => e.date.value == date)

        return elem
    }
    
    const validateTimeSerieElemChange = (newValue, date) => {
        triggerSave();

        const elem = getElembyDate(date)
        
        apiService.putTimeSerieElem(field.id, elem.date.value, newValue !== "" ? newValue.replace(',', '.') : null).then(async(response) => {
            if (response)
            {
                if (response.ok) {
                    resolveSave();
                    elem.value.value = newValue !== "" ? newValue : null;
                    invalidateQueries();
                } 
            }
            reloadLocalState();
        })
    }

    const onSelectChange = (e) => {
        // Stale compute errors must be cleared upfront — the next compute will
        // either confirm the field is fine (no new error written) or replace
        // this one. Matches every other input field's change handler.
        field.compute_error = null;

        if (e.target.value == 'null') {

            apiService.postModelDataTimingValue(field.id, 'timing').then(async(response) => {
                if (response) {
                    const timing = await response.json()

                    field.currentValue.value.timing = timing.current_value;

                    field.currentValue.value.timing.periodicity.value = (timing.current_value.periodicity.value) ? timing.current_value.periodicity.value : 'null';

                    field.currentValue.value.timing_id = timing.id;

                    field.currentValue.timingChoice = e.target.value;

                    invalidateQueries();
                    updateTimeSerie();
                }

            })
        }
        else {
            const selectedTiming = e.target.value;
            const newTimingId = selectedTiming.id;

            apiService.putTimeSerie(field.id, field.currentValue.value.unit.value, newTimingId).then(
                async(response) => {
                    if (response) {
                        invalidateQueries();
                        field.currentValue.value.timing_id = newTimingId;
                        field.currentValue.timingChoice = selectedTiming;
                        // Reflect the referenced timing's dates immediately so the
                        // read-only Start/End display updates without needing a
                        // leave-and-return refetch (covers the global timing too).
                        // Periodicity is kept from the existing timing for the
                        // global case, where the option carries the field's own.
                        field.currentValue.value.timing = {
                            start_date: { value: selectedTiming.start_date ? dayjs(selectedTiming.start_date) : null, address: null },
                            end_date: { value: selectedTiming.end_date ? dayjs(selectedTiming.end_date) : null, address: null },
                            periodicity: { value: selectedTiming.periodicity || 'null', address: null },
                            custom: false,
                        };
                        updateTimeSerie();
                    }
                }
            )
        }
    }


    const timeSerieData = formatTimeSerieObj(field)

    const columns: TailwindDataGridColumn[] = [
        {
          field: 'name',
          headerName: '',
          width: 60,
          renderCell: (params) => renderNameCell(params),
          renderHeader: () => renderNameHeader(),
          sortable: false,
        },
        ...Object.keys(timeSerieData)
            .filter(key => key !== 'name')
            .map((key, index) => ({
                field: key,
                headerName: key,
                width: 120,
                renderCell: (params) => renderNumberCell(params, index),
                renderHeader: () => renderDateHeader(key),
                sortable: false,
            }))
      ];


    const timingOptions = [
        { value: 'null', label: 'Custom' },
        ...timingChoicesItems.map((e: any) => ({
            value: e.id.toString(),
            // The scenario-level global timing is a virtual reference (id 'global').
            label: e.id === 'global'
                ? 'Global Timing'
                : `${e.parent_name} | ${snakeToPascal(e.field_name)}`,
        }))
    ];

    // Derive the dropdown selection from the persistent state (`timing_id` +
    // the loaded options) rather than the transient `timingChoice` flag.
    // `initTimeSerie` runs on every refresh and resets `timingChoice` to
    // 'null', which would otherwise flip the dropdown back to "Custom" right
    // after the user picked a reference.
    const matchedTimingItem: any = field.currentValue?.value?.timing_id
        ? timingChoicesItems.find((t: any) => t.id == field.currentValue.value.timing_id)
        : null;
    const isCustomTiming = !matchedTimingItem;
    const currentTimingValue = matchedTimingItem ? matchedTimingItem.id.toString() : 'null';

    // When a timing reference is selected, the field's own `currentValue.value.timing`
    // does not reliably carry the referenced timing's dates (unlike
    // customizable_timing_reference). Source them from the matched timing option,
    // which always includes start_date/end_date/periodicity (ISO strings).
    const startCurrentValue = isCustomTiming
        ? ((field.currentValue?.value?.timing?.start_date && field.currentValue.value.timing.start_date.value) ? field.currentValue.value.timing.start_date.value : null)
        : (matchedTimingItem?.start_date ? dayjs(matchedTimingItem.start_date) : null);
    const endCurrentValue = isCustomTiming
        ? ((field.currentValue?.value?.timing?.end_date && field.currentValue.value.timing.end_date.value) ? field.currentValue.value.timing.end_date.value : null)
        : (matchedTimingItem?.end_date ? dayjs(matchedTimingItem.end_date) : null);
    const refPeriodicity = matchedTimingItem?.periodicity ?? null;

    // Key that changes when timing or data changes, forcing full remount
    // of date pickers and data grid (their internal state can go stale).
    const tsStart = field.currentValue?.value?.timing?.start_date?.value;
    const tsEnd = field.currentValue?.value?.timing?.end_date?.value;
    const remountKey = `${field.currentValue?.value?.data?.length}-${tsStart && typeof tsStart === 'object' && tsStart.format ? tsStart.format('YYYY-MM-DD') : tsStart}-${tsEnd && typeof tsEnd === 'object' && tsEnd.format ? tsEnd.format('YYYY-MM-DD') : tsEnd}`;

    return (
        <div className="ml-3" key={remountKey}>
            <div className="flex flex-row mt-3">
                <div>
                    <div className="mb-1">
                        Timing :
                    </div>
                    <TailwindSelect
                        value={currentTimingValue}
                        onChange={(newValue) => {
                            // Create synthetic event-like object for onSelectChange
                            onSelectChange({ target: { value: newValue === 'null' ? 'null' : timingChoicesItems.find((t: any) => t.id.toString() === newValue) } });
                        }}
                        options={timingOptions}
                        disabled={isReadOnly || timingOptionsLoading}
                        className="w-[200px]"
                    />
                </div>
                {/* Start/End date and periodicity are always shown. When a timing
                    reference is selected (`!isCustomTiming`) they come from the
                    referenced timing and are displayed read-only; only the custom
                    case is editable. */}
                <div className="ml-4 -mt-7">
                    <div className="mb-3.5">
                        <div className="mb-1">
                            Start Date
                        </div>
                        <CustomDatePicker
                            item={field.start_dateStore}
                            currentValue={startCurrentValue}
                            handleChange={handleChangestart_date}
                            readOnly={!isCustomTiming}
                        />
                    </div>
                    <div className="mb-3.5">
                        <div className="mb-1">
                            End Date
                        </div>
                        <CustomDatePicker
                            item={field.end_dateStore}
                            currentValue={endCurrentValue}
                            handleChange={handleChangeend_date}
                            readOnly={!isCustomTiming}
                        />
                    </div>
                    {
                        (isCustomTiming && field.timingError) ?
                        <div className="text-red-600">
                            {field.timingError}
                        </div> : <div></div>
                    }
                    <div className="mb-1">
                        Periodicity
                    </div>
                    {
                        isCustomTiming ?
                            <TailwindSelect
                                value={field.currentValue?.value?.timing?.periodicity?.value && field.currentValue.value.timing.periodicity.value !== 'null' ? field.currentValue.value.timing.periodicity.value : ''}
                                onChange={(newValue) => {
                                    // Create synthetic event-like object for handleChangePeriodicity
                                    handleChangePeriodicity({ target: { value: newValue || 'null' } });
                                }}
                                options={field.periodicity_choices.map(e => ({ value: e, label: snakeToPascal(e) }))}
                                placeholder="Periodicity"
                                disabled={isReadOnly}
                                className="w-[200px]"
                            /> :
                            <div
                                className="w-[200px] flex items-center justify-between bg-slate-100 border border-dashed border-slate-300 text-slate-500 text-sm rounded-lg px-4 py-2.5 font-medium cursor-not-allowed select-none"
                                title="Inherited from the referenced timing"
                            >
                                <span>{refPeriodicity && refPeriodicity !== 'null' ? snakeToPascal(refPeriodicity) : '—'}</span>
                                <span className="material-symbols-outlined text-[16px] text-slate-400">lock</span>
                            </div>
                    }
                </div>
            </div>
            {/* Reserve a fixed-height row for the save indicator so showing/
                hiding it doesn't push the data grid up and down. */}
            <div className="h-6 mt-2 flex items-center">
                <SaveIndicator status={saveStatus} />
            </div>
            <div
                className="mt-3 mr-2"
                tabIndex={0}
                onPaste={(e: React.ClipboardEvent) => {
                    const text = e.clipboardData.getData('text/plain');
                    if (text.includes('\t') && focusedColumnIndex !== null) {
                        e.preventDefault();
                        handleMultiCellPaste(text, focusedColumnIndex);
                    }
                }}
            >
                <TailwindDataGrid
                    rows={[timeSerieData]}
                    columns={columns}
                    density="comfortable"
                    hideFooter={true}
                />
            </div>
            <FieldError field={field} />

            {/* Modal de choix de conversion lors du changement de periodicite */}
            <TailwindModal
                isOpen={periodicityModalOpen}
                onClose={handlePeriodicityModalClose}
                title="Convert existing values?"
                description={
                    pendingPeriodicityChange
                        ? `Periodicity changed from ${snakeToPascal(pendingPeriodicityChange.oldPeriodicity)} to ${snakeToPascal(pendingPeriodicityChange.newPeriodicity)}. ${pendingPeriodicityChange.direction === 'coarseToFine' ? 'How should existing values be distributed?' : 'How should values be consolidated?'}`
                        : ''
                }
                size="md"
                footer={
                    pendingPeriodicityChange?.direction === 'coarseToFine' ? (
                        <div className="flex gap-3 justify-center w-full">
                            <TailwindButton variant="secondary" onClick={handlePeriodicityModalClose} icon="restart_alt">
                                Reset values
                            </TailwindButton>
                            <TailwindButton variant="primary" onClick={() => applyPeriodicityConversion('divide')} icon="call_split">
                                Divide equally
                            </TailwindButton>
                            <TailwindButton variant="dark" onClick={() => applyPeriodicityConversion('copy')} icon="content_copy">
                                Copy to all
                            </TailwindButton>
                        </div>
                    ) : (
                        <div className="flex gap-3 justify-center w-full">
                            <TailwindButton variant="secondary" onClick={handlePeriodicityModalClose} icon="restart_alt">
                                Reset values
                            </TailwindButton>
                            <TailwindButton variant="primary" onClick={() => applyPeriodicityConversion('sum')} icon="functions">
                                Sum values
                            </TailwindButton>
                            <TailwindButton variant="dark" onClick={() => applyPeriodicityConversion('average')} icon="calculate">
                                Average values
                            </TailwindButton>
                        </div>
                    )
                }
            >
                {pendingPeriodicityChange?.direction === 'coarseToFine' ? (
                    <div className="text-slate-700 space-y-3">
                        <p>
                            <strong>Divide equally</strong>: Each value is divided by the number of sub-periods.<br />
                            <span className="text-slate-500 text-sm">
                                Example: 1,200 (annual) → 100 per month
                            </span>
                        </p>
                        <p>
                            <strong>Copy to all</strong>: Each value is copied identically to every sub-period.<br />
                            <span className="text-slate-500 text-sm">
                                Example: 1,200 (annual) → 1,200 per month
                            </span>
                        </p>
                    </div>
                ) : (
                    <div className="text-slate-700 space-y-3">
                        <p>
                            <strong>Sum values</strong>: All sub-period values are added together.<br />
                            <span className="text-slate-500 text-sm">
                                Example: 100 × 12 months → 1,200 (annual)
                            </span>
                        </p>
                        <p>
                            <strong>Average values</strong>: The mean of all sub-period values is used.<br />
                            <span className="text-slate-500 text-sm">
                                Example: 100 × 12 months → 100 (annual)
                            </span>
                        </p>
                    </div>
                )}
            </TailwindModal>
        </div>
    )
}
